import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	Collapse,
	Divider,
	Tooltip,
	Skeleton,
	Avatar,
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
} from "@mui/material";
import { CompareArrows, KeyboardArrowDown, Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import axios from "axios";
import OthersBreakdown from "./OthersBreakdown";

/* -----------------------------
   Utils
----------------------------- */
const formatToCrore = (amount = 0) => `${(amount / 10000000).toFixed(2)} Cr`;

/* -----------------------------
   Skeleton
----------------------------- */
const ComparisonSkeleton = () => (
	<Card sx={{ height: "100%", borderRadius: 3, boxShadow: "none", border: "1px solid #e0e0e0" }}>
		<CardContent>
			<Box display="flex" alignItems="center" mb={2}>
				<Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
				<Skeleton width="60%" height={20} />
			</Box>
			<Skeleton width="100%" height={60} />
			<Skeleton width="40%" height={20} sx={{ mt: 1 }} />
		</CardContent>
	</Card>
);

/* -----------------------------
   Component
----------------------------- */
const LedgerVsMopSummary = ({ startDate, endDate, runKey, isActive, onActivate }) => {
	const [loading, setLoading] = useState(false);
	const [summary, setSummary] = useState(null);
	const [showBreakup, setShowBreakup] = useState(false);
	const [openOthersDialog, setOpenOthersDialog] = useState(false);

	useEffect(() => {
		if (!runKey) return;

		const fetchSummary = async () => {
			setLoading(true);
			setShowBreakup(false);

			try {
				const { data } = await axios.post("/api/pos_reco/get-bank-statement-summary", {
					start_date: startDate,
					end_date: endDate,
				});
				setSummary(data);
			} catch (err) {
				console.error("Failed to load Ledger vs MOP summary", err);
				setSummary(null);
			} finally {
				setLoading(false);
			}
		};

		fetchSummary();
	}, [runKey]);

	/* -----------------------------
	   Zero-safe fallback
	----------------------------- */
	const safeSummary = summary || {
		totals: {
			ledger_sale: 0,
			bank_statement_sale: 0,
			variance: 0,
		},
		breakdown: {
			bank_statement_sale: [],
			ledger_sale: [],
		},
	};

	const { ledger_sale, bank_statement_sale, variance } = safeSummary.totals;
	const hasVariance = variance > 0;

	return (
		<Box mt={0} width="100%">
			{/* ================= LOADING ================= */}
			{loading && <ComparisonSkeleton />}

			{/* ================= SUMMARY ================= */}
			{!loading && (
				<>
					<Card
						elevation={0}
						sx={{
							cursor: "pointer",
							mb: 2,
							borderRadius: 3,
							border: "1px solid #e0e0e0",
							borderLeft: `4px solid ${hasVariance ? "#d32f2f" : "#2e7d32"}`,
							transition: "all 0.3s ease",
							"&:hover": {
								boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
								transform: "translateY(-2px)",
							},
						}}
						onClick={() => {
							onActivate?.();
							setShowBreakup((prev) => !prev);
						}}>
						<CardContent>
							<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
								<Box display="flex" alignItems="center" gap={2}>
									<Avatar
										variant="rounded"
										sx={{
											bgcolor: hasVariance ? "#ffebee" : "#e8f5e9",
											color: hasVariance ? "#d32f2f" : "#2e7d32",
											width: 40,
											height: 40,
											borderRadius: 2,
										}}>
										<CompareArrows sx={{ fontSize: 28 }} />
									</Avatar>
									<Box>
										<Typography variant="subtitle2" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
											LEDGER VS Bank Statement
										</Typography>
										{/* <Typography variant="caption" color="text.secondary" fontWeight={500}>
											{startDate} - {endDate}
										</Typography> */}
									</Box>
								</Box>
								<Box
									sx={{
										transform: showBreakup ? "rotate(180deg)" : "rotate(0deg)",
										transition: "transform 0.3s",
									}}>
									<KeyboardArrowDown color="action" />
								</Box>
							</Box>

							<Grid container spacing={2} alignItems="center">
								<Grid item xs={4}>
									<Stack alignItems="center" spacing={0.5}>
										<Typography variant="caption" color="text.secondary">
											Ledger Sale
										</Typography>
										<Tooltip title={`₹ ${ledger_sale?.toLocaleString("en-IN")}`} arrow>
											<Typography variant="h6" fontWeight={600} color="text.primary">
												₹ {formatToCrore(ledger_sale)}
											</Typography>
										</Tooltip>
									</Stack>
								</Grid>

								<Grid item xs={4}>
									<Stack alignItems="center" spacing={0.5}>
										<Typography variant="caption" color="text.secondary">
											Bank Statement
										</Typography>
										<Tooltip title={`₹ ${bank_statement_sale?.toLocaleString("en-IN")}`} arrow>
											<Typography variant="h6" fontWeight={600} color="text.primary">
												₹ {formatToCrore(bank_statement_sale)}
											</Typography>
										</Tooltip>
									</Stack>
								</Grid>

								<Grid item xs={4}>
									<Stack alignItems="center" spacing={0.5}>
										<Typography variant="caption" color="text.secondary">
											Variance
										</Typography>
										<Tooltip title={`₹ ${variance?.toLocaleString("en-IN")}`} arrow>
											<Typography
												variant="h6"
												fontWeight={600}
												color={hasVariance ? "error.main" : "success.main"}>
												₹ {Number(variance).toFixed(2)}
											</Typography>
										</Tooltip>
									</Stack>
								</Grid>
							</Grid>

							<Box mt={3} display="block">
								<Typography
									variant="caption"
									color="primary"
									fontWeight={600}
									sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{showBreakup ? "Hide Breakdown" : "View Detailed Breakdown"}
								</Typography>
							</Box>
						</CardContent>
					</Card>

					{/* ================= BREAKDOWN ================= */}
					<Collapse in={showBreakup}>
						<Card
							elevation={0}
							sx={{ border: "1px solid #e0e0e0", borderRadius: 3, mb: 2, bgcolor: "#fff", overflow: "hidden" }}>
							<Box sx={{ bgcolor: "#f5f7fa", px: 3, py: 2, borderBottom: "1px solid #eee" }}>
								<Typography variant="subtitle2" fontWeight={700} color="text.primary" letterSpacing={0.5}>
									DETAILED BREAKDOWN
								</Typography>
							</Box>
							<CardContent sx={{ p: 0 }}>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "1.5fr 1fr 1fr 1fr",
										gap: 2,
										px: 3,
										py: 1.5,
										bgcolor: "#fafafa",
										borderBottom: "1px solid #eee",
									}}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										MOP TYPE
									</Typography>
									<Typography variant="caption" fontWeight={700} color="text.secondary" textAlign="right">
										LEDGER
									</Typography>
									<Typography variant="caption" fontWeight={700} color="text.secondary" textAlign="right">
										Bank statement
									</Typography>
									<Typography variant="caption" fontWeight={700} color="text.secondary" textAlign="right">
										VARIANCE
									</Typography>
								</Box>

								<Stack spacing={0} divider={<Divider />}>
									{safeSummary.breakdown.bank_statement_sale.map((mopRow) => {
										const ledgerAmount =
											safeSummary.breakdown.ledger_sale.find((r) => r.mop === mopRow.mop)?.amount || 0;
										const diff = ledgerAmount - mopRow.amount;
										const isMatch = Math.abs(diff) < 0.01;
										const isOthers = mopRow.mop === "OTHERS";

										return (
											<Box
												key={mopRow.mop}
												onClick={() => isOthers && setOpenOthersDialog(true)}
												sx={{
													display: "grid",
													gridTemplateColumns: "1.5fr 1fr 1fr 1fr",
													gap: 2,
													px: 3,
													py: 2,
													transition: "all 0.2s",
													cursor: isOthers ? "pointer" : "default",
													"&:hover": {
														bgcolor: isOthers ? "#f0f0f0" : "#f9f9f9",
													},
												}}>
												<Box display="flex" alignItems="center">
													<Typography
														variant="body2"
														fontWeight={600}
														color={isOthers ? "primary.main" : "text.primary"}
														sx={{ textDecoration: isOthers ? "underline" : "none" }}>
														{mopRow.mop}
													</Typography>
												</Box>
												<Typography
													variant="body2"
													color="text.secondary"
													textAlign="right"
													sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
													{ledgerAmount.toLocaleString("en-IN")}
												</Typography>
												<Typography
													variant="body2"
													color="text.secondary"
													textAlign="right"
													sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
													{mopRow.amount.toLocaleString("en-IN")}
												</Typography>
												<Typography
													variant="body2"
													fontWeight={700}
													color={isMatch ? "success.main" : "error.main"}
													textAlign="right"
													sx={{ fontVariantNumeric: "tabular-nums" }}>
													{!isMatch && diff > 0 ? "+" : ""}
													{isMatch ? "0" : diff.toLocaleString("en-IN")}
												</Typography>
											</Box>
										);
									})}
								</Stack>
							</CardContent>
						</Card>
					</Collapse>

					{/* ================= OTHERS BREAKDOWN DIALOG ================= */}
					<Dialog open={openOthersDialog} onClose={() => setOpenOthersDialog(false)} maxWidth="md" fullWidth>
						<DialogTitle>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Typography variant="h6">Others Breakdown</Typography>
								<IconButton onClick={() => setOpenOthersDialog(false)}>
									<Close />
								</IconButton>
							</Box>
						</DialogTitle>
						<DialogContent>
							<OthersBreakdown startDate={startDate} endDate={endDate} open={openOthersDialog} />
						</DialogContent>
					</Dialog>
				</>
			)}
		</Box>
	);
};

export default LedgerVsMopSummary;
